import {ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {
  BusinessProductDto,
  CertificateItemDto,
  ExportCheckpointDto,
  RoleAssignmentDto,
  TradeHarvestRequest,
  TradeRecordDto,
  TradeRecordUpdateRequest
} from '../../../../models/generated-models';
import {GtNgDatepickerComponent} from '../../../shared/commons/gt-ng-datepicker/gt-ng-datepicker.component';
import {UserCertificateApiService} from '../../user-certificate/user-certificate-api.service';
import {TradeApiService} from '../trade-api.service';
import {TradeStore} from '../trade.store';
import {TradeBusinessSelectComponent} from '../trade-business-select.component';
import {DEFAULT_TRADE_UNIT, TRADE_BASE, TRADE_CERTIFICATION, tradeKindLabel} from '../trade.constants';
import {normalizeQuantityInput, parseQuantity, todayIsoDate} from '../trade-utils';

export interface CertificateOption {
  key: string;
  certificateId: number | null;
  farmId: number | null;
  ownerName: string;
  taxId: string;
  standardCode: string;
  standardName: string;
  productName: string;
  description: string;
  certificateNo: string;
  latitude: string;
  longitude: string;
}

/** One ticked certificate — kept index-aligned when the record is saved. */
export interface SelectedCertificate {
  certificateNo: string;
  standardName: string;
}

/** Same normalization as the backend `RoleAssignmentService#normalizeCertificateNo`. */
function normalizeCertificateNo(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase().replace(/\s+/g, '');
}

/**
 * Product-driven harvest form: step 2 picks the product being sold (one of the
 * business's remembered products, or a new name), step 3 fills the details and
 * ticks the certificates the product is sold under — the backend remembers that
 * binding and prefills it the next time. In edit mode the same page edits the
 * descriptive fields of any kind.
 */
@Component({
  selector: 'app-trade-harvest-form',
  standalone: true,
  imports: [
    FormsModule,
    RouterLink,
    GtNgDatepickerComponent,
    TradeBusinessSelectComponent
  ],
  templateUrl: './trade-harvest-form.component.html',
  styleUrl: './trade-harvest-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TradeHarvestFormComponent implements OnInit {
  private readonly certificateApi = inject(UserCertificateApiService);
  private readonly api = inject(TradeApiService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  protected readonly store = inject(TradeStore);

  protected readonly recordId = signal<number | null>(null);
  protected readonly record = signal<TradeRecordDto | null>(null);
  protected readonly loading = signal(false);
  protected readonly saving = signal(false);
  protected readonly submitted = signal(false);
  protected readonly step = signal<0 | 1 | 2>(0);

  /** The business's remembered products (step 2). */
  protected readonly products = signal<BusinessProductDto[]>([]);
  protected readonly productsLoading = signal(false);
  protected readonly productNameSuggestions = signal<string[]>([]);
  protected readonly newProductName = signal('');

  /** Every certificate the user may use, already scoped to the acting grant. */
  protected readonly certificateOptions = signal<CertificateOption[]>([]);
  protected readonly certificatesLoading = signal(false);
  /** The ticked certificates, in the order they are sent to the backend. */
  protected readonly certSelection = signal<SelectedCertificate[]>([]);

  // Plain form model — every field is bound with ngModel.
  protected form = {
    productName: '',
    quantity: '',
    harvestDate: todayIsoDate() as string | null,
    lotCode: '',
    farmName: '',
    farmLocation: '',
    packingNote: '',
    // non-harvest descriptive fields, used by edit mode only
    contactPhone: '',
    truckSide: '' as '' | 'BUYER' | 'SELLER',
    truckNo: '',
    // export attributes — only an export record (a ผู้ส่งออก purchase) carries them
    exportCheckpointId: '',
    exporterRegistrationNo: '',
    /** The record sits on the seller's pin instead of its own business's (work item 179). */
    useSellerLocation: false,
    containerNo: '',
    sealNo: '',
    qualityNote: ''
  };

  /** The ด่านส่งออก master, loaded only while editing an export record. */
  protected readonly checkpoints = signal<ExportCheckpointDto[]>([]);

  /** The only unit in the system — shown, never typed. */
  protected readonly unit = DEFAULT_TRADE_UNIT;

  protected readonly tradeKindLabel = tradeKindLabel;
  protected readonly isEdit = computed(() => this.recordId() != null);
  protected readonly editKind = computed(() => this.record()?.kind ?? 'HARVEST');
  /** An exporter's PURCHASE ends the chain and owns the export attributes. */
  protected readonly isExportRecord = computed(() => this.record()?.export === true);
  /** Quantity may only change while nothing has been consumed. */
  /**
   * Product name and quantity follow the backend rule (`quantityEditable`): an origin lot that
   * nothing has drawn from and nothing is committed on. Otherwise both inputs are locked, so what
   * the form shows is what the save will do.
   */
  protected readonly canEditQuantity = computed(
    () => !this.isEdit() || this.record()?.quantityEditable === true
  );
  protected readonly needsBusinessStep = computed(
    () => !this.isEdit() && this.store.businesses().length > 1
  );
  /** Only the selected business's own profile types — nothing else. */
  protected readonly businessTypeOptions = computed(() => {
    return uniqueTitles(this.store.selectedBusiness()?.businessTypeTitles);
  });
  protected readonly selectedBusinessType = signal<string | null>(null);
  /** A type pick is required whenever the picked business offers any types. */
  protected readonly needsBusinessType = computed(() => this.businessTypeOptions().length > 0);

  /**
   * The certificates offered on the details step: the business's own ones, or
   * all of the user's when nothing is attached to the business yet.
   */
  protected readonly businessCertificates = computed<CertificateOption[]>(() => {
    const all = this.certificateOptions();
    const attached = new Set(
      (this.store.selectedBusiness()?.certificateNos ?? [])
        .map((no) => normalizeCertificateNo(no))
        .filter(Boolean)
    );
    if (attached.size === 0) {
      return all;
    }
    return all.filter((option) => attached.has(normalizeCertificateNo(option.certificateNo)));
  });

  protected get canContinueFromBusiness(): boolean {
    return this.store.selectedBusinessId() != null
      && (!this.needsBusinessType() || this.selectedBusinessType() != null);
  }

  protected get canUseNewProduct(): boolean {
    return !!this.newProductName().trim();
  }

  private loadedAssignmentKey: number | null | undefined;
  private loadedProductsKey: number | null | undefined;

  constructor() {
    effect(() => {
      // A business with exactly one type has nothing to choose: pick it for the user.
      // A stale pick that the new business does not offer is dropped.
      const options = this.businessTypeOptions();
      const current = this.selectedBusinessType();
      if (options.length === 1 && current !== options[0]) {
        this.selectedBusinessType.set(options[0]);
      } else if (current != null && !options.includes(current)) {
        this.selectedBusinessType.set(null);
      }
    });
    effect(() => {
      // Reload the certificate list when the acting grant changes: an agent
      // sees the grantor's certificates, not their own.
      const assignment = this.store.activeRoleAssignment();
      const key = assignment?.id ?? null;
      if (key === this.loadedAssignmentKey) {
        return;
      }
      this.loadedAssignmentKey = key;
      this.loadCertificates(assignment);
    });
    effect(() => {
      // The remembered products belong to the picked business.
      if (this.isEdit()) {
        return;
      }
      const businessId = this.store.selectedBusinessId();
      if (businessId === this.loadedProductsKey) {
        return;
      }
      this.loadedProductsKey = businessId;
      if (businessId == null) {
        this.products.set([]);
        this.productsLoading.set(false);
        return;
      }
      this.loadProducts(businessId);
    });
  }

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const id = idParam == null ? null : Number(idParam);
    this.recordId.set(Number.isInteger(id) ? id : null);

    if (this.recordId() != null) {
      this.loadRecord(this.recordId()!);
      return;
    }

    this.api.listProductNames().subscribe({
      next: (names) => this.productNameSuggestions.set(names ?? []),
      error: () => this.productNameSuggestions.set([])
    });
    void this.store.loadBusinesses().then(() => {
      // Always confirm the acting business on entry, even when a previous
      // selection was remembered — a refresh must not skip this step.
      this.step.set(this.needsBusinessStep() ? 0 : 1);
    });
  }

  // --- navigation ---------------------------------------------------------

  protected onBusinessChange(businessId: number | null): void {
    this.store.selectBusiness(businessId);
    this.selectedBusinessType.set(null);
  }

  protected continueFromBusiness(): void {
    if (!this.canContinueFromBusiness) {
      return;
    }
    this.step.set(1);
  }

  protected backToBusiness(): void {
    this.step.set(this.needsBusinessStep() ? 0 : 1);
  }

  /** Label under a product card. */
  protected certificateCountLabel(product: BusinessProductDto): string {
    const count = product.certificates?.length ?? 0;
    return count > 0 ? `${count} ใบรับรอง` : 'ยังไม่ผูกใบรับรอง';
  }

  /** A remembered product carries its certificate binding to the details step. */
  protected chooseProduct(product: BusinessProductDto): void {
    this.form.productName = product.productName ?? '';
    this.certSelection.set(
      (product.certificates ?? [])
        .map((item) => ({
          certificateNo: (item.certificateNo ?? '').trim(),
          standardName: (item.standardName ?? '').trim()
        }))
        .filter((item) => !!item.certificateNo)
    );
    this.applyFarmPrefill();
    this.submitted.set(false);
    this.step.set(2);
  }

  /** A typed name: reuse the binding when it is a known product after all. */
  protected chooseNewProduct(): void {
    const name = this.newProductName().trim();
    if (!name) {
      return;
    }
    const known = this.products().find(
      (product) => (product.productName ?? '').trim().toLowerCase() === name.toLowerCase()
    );
    if (known) {
      this.chooseProduct(known);
      return;
    }
    this.form.productName = name;
    this.certSelection.set([]);
    this.submitted.set(false);
    this.step.set(2);
  }

  protected backToProduct(): void {
    this.step.set(1);
    this.submitted.set(false);
  }

  // --- certificates -------------------------------------------------------

  protected isCertificateChecked(option: CertificateOption): boolean {
    const key = normalizeCertificateNo(option.certificateNo);
    return this.certSelection().some((item) => normalizeCertificateNo(item.certificateNo) === key);
  }

  protected toggleCertificate(option: CertificateOption): void {
    const key = normalizeCertificateNo(option.certificateNo);
    this.certSelection.update((current) => {
      if (current.some((item) => normalizeCertificateNo(item.certificateNo) === key)) {
        return current.filter((item) => normalizeCertificateNo(item.certificateNo) !== key);
      }
      return [...current, {certificateNo: option.certificateNo, standardName: option.standardName}];
    });
    this.applyFarmPrefill();
  }

  /** The farm name follows the first ticked certificate while it is untouched. */
  private applyFarmPrefill(): void {
    if (this.isEdit() || this.form.farmName.trim()) {
      return;
    }
    const first = this.certSelection()[0];
    if (!first) {
      return;
    }
    const key = normalizeCertificateNo(first.certificateNo);
    const match = this.certificateOptions().find(
      (option) => normalizeCertificateNo(option.certificateNo) === key
    );
    if (match) {
      this.form.farmName = match.ownerName;
    }
  }

  // --- validation ---------------------------------------------------------

  protected get hasProductName(): boolean {
    return !!this.form.productName.trim();
  }

  protected get hasQuantity(): boolean {
    return parseQuantity(this.form.quantity) > 0;
  }

  protected get hasHarvestDate(): boolean {
    return !!this.form.harvestDate?.trim();
  }

  protected get canSubmit(): boolean {
    if (this.isEdit()) {
      return this.editKind() !== 'HARVEST'
        || (this.hasProductName && (!this.canEditQuantity() || this.hasQuantity));
    }
    return this.hasProductName && this.hasQuantity
      && this.hasHarvestDate && this.store.selectedBusinessId() != null;
  }

  protected onQuantityInput(): void {
    this.form.quantity = normalizeQuantityInput(this.form.quantity);
  }

  // --- submit -------------------------------------------------------------

  protected submit(): void {
    this.submitted.set(true);
    if (this.saving() || !this.canSubmit || this.truckFieldsMissing()) {
      return;
    }
    this.saving.set(true);
    if (this.isEdit()) {
      this.api.updateRecord(this.recordId()!, this.buildUpdateRequest()).subscribe({
        next: (saved) => this.goToDetail(saved.id ?? this.recordId()!),
        error: () => this.saving.set(false)
      });
      return;
    }
    this.api.createHarvest(this.buildHarvestRequest()).subscribe({
      next: (saved) => this.goToSellFromHarvest(saved),
      error: () => this.saving.set(false)
    });
  }

  /**
   * "สร้างรายการใหม่" ends on the ใบขาย for the lot just recorded (product and quantity
   * prefilled) — a farmer records to sell, and a buyer can only scan an offer. The sell page
   * offers a way to skip to the record instead.
   */
  /** A moved leg (purchase / shipment) must name the truck and whose it is. */
  protected truckFieldsMissing(): boolean {
    if (!this.isEdit() || (this.editKind() !== 'PURCHASE' && this.editKind() !== 'SHIPMENT') || this.exporterOnlyRecord()) {
      return false;
    }
    return !this.form.truckNo.trim() || !this.form.truckSide;
  }

  /** A pure exporter's record: no truck, container, seal or quality inputs. */
  protected readonly exporterOnlyRecord = computed(() => this.record()?.exporterOnlyBusiness === true);

  private goToSellFromHarvest(saved: TradeRecordDto): void {
    this.saving.set(false);
    if (saved.id == null) {
      void this.router.navigate([TRADE_BASE]);
      return;
    }
    void this.router.navigate([TRADE_BASE, 'sell'], {
      queryParams: {
        productName: saved.productName ?? this.form.productName.trim(),
        quantity: saved.quantity ?? this.form.quantity,
        fromRecord: saved.id
      }
    });
  }

  /** `certificateNos[i]` and `standardList[i]` describe the same certificate. */
  private selectedCertificateNos(): string[] {
    return this.certSelection().map((item) => item.certificateNo);
  }

  private selectedStandardNames(): string[] {
    return this.certSelection().map((item) => item.standardName);
  }

  private buildHarvestRequest(): TradeHarvestRequest {
    return {
      businessId: this.store.selectedBusinessId() ?? undefined,
      productName: this.form.productName.trim(),
      quantity: parseQuantity(this.form.quantity),
      lotCode: this.form.lotCode.trim() || undefined,
      harvestDate: (this.form.harvestDate?.trim() || undefined) as unknown as Date,
      farmName: this.form.farmName.trim() || undefined,
      farmLocation: this.form.farmLocation.trim() || undefined,
      standardList: this.selectedStandardNames(),
      certificateNos: this.selectedCertificateNos(),
      packingNote: this.form.packingNote.trim() || undefined,
      certification: TRADE_CERTIFICATION.NONE
    };
  }

  private buildUpdateRequest(): TradeRecordUpdateRequest {
    const request: TradeRecordUpdateRequest = {
      productName: this.form.productName.trim() || undefined,
      lotCode: this.form.lotCode.trim() || undefined,
      harvestDate: (this.form.harvestDate?.trim() || undefined) as unknown as Date,
      farmName: this.form.farmName.trim() || undefined,
      farmLocation: this.form.farmLocation.trim() || undefined,
      standardList: this.selectedStandardNames(),
      certificateNos: this.selectedCertificateNos(),
      packingNote: this.form.packingNote.trim() || undefined,
      contactPhone: this.form.contactPhone.trim() || undefined,
      truckNo: this.form.truckNo.trim() || undefined,
      truckSide: this.form.truckSide || undefined
    };
    if (this.isExportRecord()) {
      const checkpointId = Number(this.form.exportCheckpointId);
      request.exportCheckpointId = Number.isInteger(checkpointId) && checkpointId > 0
        ? checkpointId
        : undefined;
      request.exporterRegistrationNo = this.form.exporterRegistrationNo.trim() || undefined;
      request.useSellerLocation = this.form.useSellerLocation;
      request.containerNo = this.form.containerNo.trim() || undefined;
      request.sealNo = this.form.sealNo.trim() || undefined;
      request.qualityNote = this.form.qualityNote.trim() || undefined;
    }
    if (this.canEditQuantity()) {
      request.quantity = parseQuantity(this.form.quantity);
    }
    return request;
  }

  private goToDetail(id: number): void {
    this.saving.set(false);
    void this.router.navigate([TRADE_BASE, id]);
  }

  // --- loading ------------------------------------------------------------

  private loadRecord(id: number): void {
    this.loading.set(true);
    this.step.set(2);
    this.api.getRecord(id).subscribe({
      next: (record) => {
        if (record.canEdit === false) {
          // Read-only for this caller (an officer, or somebody else's record): the edit form has
          // nothing to offer, and the backend would refuse the save anyway (work item 190).
          void this.router.navigate([TRADE_BASE, id], {replaceUrl: true});
          return;
        }
        this.record.set(record);
        this.applyRecord(record);
        this.loading.set(false);
      },
      error: () => {
        this.record.set(null);
        this.loading.set(false);
      }
    });
  }

  private applyRecord(record: TradeRecordDto): void {
    this.form = {
      ...this.form,
      productName: record.productName ?? '',
      quantity: record.quantity == null ? '' : String(record.quantity),
      harvestDate: toIsoDate(record.harvestDate),
      lotCode: record.lotCode ?? '',
      farmName: record.farmName ?? '',
      farmLocation: record.farmLocation ?? '',
      packingNote: record.packingNote ?? '',
      contactPhone: record.contactPhone ?? '',
      truckSide: (record.truckSide ?? '') as '' | 'BUYER' | 'SELLER',
      truckNo: record.truckNo ?? '',
      exportCheckpointId: record.exportCheckpointId == null ? '' : String(record.exportCheckpointId),
      exporterRegistrationNo: record.exporterRegistrationNo ?? '',
      useSellerLocation: record.locationFromSeller === true,
      containerNo: record.containerNo ?? '',
      sealNo: record.sealNo ?? '',
      qualityNote: record.qualityNote ?? ''
    };
    if (record.export === true && this.checkpoints().length === 0) {
      this.api.listExportCheckpoints().subscribe({
        next: (items) => this.checkpoints.set(items ?? []),
        error: () => this.checkpoints.set([])
      });
    }
    const standards = record.standardList ?? [];
    this.certSelection.set(
      (record.certificateNos ?? [])
        .map((no, index) => ({
          certificateNo: (no ?? '').trim(),
          standardName: (standards[index] ?? '').trim()
        }))
        .filter((item) => !!item.certificateNo)
    );
  }

  private loadProducts(businessId: number): void {
    this.productsLoading.set(true);
    this.api.listProducts(businessId).subscribe({
      next: (items) => {
        this.products.set(items ?? []);
        this.productsLoading.set(false);
      },
      error: () => {
        this.products.set([]);
        this.productsLoading.set(false);
      }
    });
  }

  private loadCertificates(assignment: RoleAssignmentDto | null): void {
    this.certificatesLoading.set(true);
    this.certificateApi.listCertificates().subscribe({
      next: (certificates) => {
        this.certificateOptions.set(this.toCertificateOptions(certificates ?? [], assignment));
        this.certificatesLoading.set(false);
      },
      error: () => {
        this.certificateOptions.set([]);
        this.certificatesLoading.set(false);
      }
    });
  }

  /** Maps the visible certificates, honouring the granted scope. */
  protected toCertificateOptions(
    certificates: CertificateItemDto[],
    assignment: RoleAssignmentDto | null
  ): CertificateOption[] {
    const allowedNos = new Set<string>();
    const allowedIds = new Set<number>();
    for (const raw of (assignment?.certificateNo ?? '').split(',')) {
      const normalized = normalizeCertificateNo(raw);
      if (normalized) {
        allowedNos.add(normalized);
      }
    }
    if (assignment?.certificateId != null) {
      allowedIds.add(Number(assignment.certificateId));
    }
    for (const business of assignment?.businesses ?? []) {
      for (const cert of business.certificates ?? []) {
        const normalized = normalizeCertificateNo(cert.certificateNo);
        if (normalized) {
          allowedNos.add(normalized);
        }
        if (cert.certificateId != null) {
          allowedIds.add(Number(cert.certificateId));
        }
      }
    }
    // No certificate restriction on the grant: the backend already scoped the list.
    const restricted = allowedNos.size > 0 || allowedIds.size > 0;

    const options: CertificateOption[] = [];
    for (const item of certificates) {
      if (restricted) {
        const idMatch = item.id != null && allowedIds.has(Number(item.id));
        if (!idMatch && !allowedNos.has(normalizeCertificateNo(item.certificateNo))) {
          continue;
        }
      }
      options.push({
        key: `${item.id ?? ''}-${item.certificateNo ?? options.length}`,
        certificateId: item.id == null ? null : Number(item.id),
        farmId: item.farmId == null ? null : Number(item.farmId),
        ownerName: item.ownerName?.trim() || 'ยังไม่ระบุฟาร์ม',
        taxId: item.taxId?.trim() || '',
        standardCode: item.standardCode?.trim() || '-',
        standardName: item.standardName?.trim() || '-',
        productName: item.productName?.trim() || '',
        description: item.description?.trim() || '',
        certificateNo: item.certificateNo?.trim() || '',
        latitude: item.latitude?.trim() || '',
        longitude: item.longitude?.trim() || ''
      });
    }
    return options.sort((left, right) =>
      left.standardName.localeCompare(right.standardName, 'th-TH')
      || left.certificateNo.localeCompare(right.certificateNo, 'th-TH'));
  }
}

function uniqueTitles(values: readonly (string | null | undefined)[] | null | undefined): string[] {
  const seen = new Set<string>();
  const options: string[] = [];
  for (const raw of values ?? []) {
    const title = (raw ?? '').trim();
    if (title && !seen.has(title)) {
      seen.add(title);
      options.push(title);
    }
  }
  return options;
}

function toIsoDate(value: Date | string | null | undefined): string | null {
  if (!value) {
    return null;
  }
  if (typeof value === 'string') {
    return value.slice(0, 10);
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
}
